'use client'

import React, { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import SofaBlueprint, {
  type BlueprintLayout,
} from '@/configurator/SofaBlueprint'
import type { ChangeableProperties } from '@/configurator/utils/shapeConfigUtils'
import {
  DIM_LABELS,
  resolveAnchors,
} from '@/configurator/blueprint-overlay/anchors'
import { BlueprintDimensionOverlay } from '@/configurator/blueprint-overlay/BlueprintDimensionOverlay'
import { BlueprintDimensionInput } from '@/configurator/blueprint-overlay/BlueprintDimensionInput'
import type { BlueprintDimKey } from '@/configurator/blueprint-overlay/types'

interface BlueprintViewProps {
  id: number
  changeableProperties: ChangeableProperties
  /** The type `changeableProperties` were loaded for (gates the anchors). */
  propertiesType: string | null
  height?: number
  /** Preview/debug: outline the measured shape rect. */
  debug?: boolean
  /** Share of the drawing area the shape may fill (see SofaBlueprint). */
  fillOccupancy?: number
  /** Cap on zoom-in, px per cm (see SofaBlueprint). */
  maxFillScale?: number
}

type SofaDimensionsBlueprintFieldProps = BlueprintViewProps

type DimValues = Partial<Record<BlueprintDimKey, number | null | undefined>>
type DimErrors = Partial<Record<BlueprintDimKey, string | undefined>>

interface BlueprintDimensionsEditorProps extends BlueprintViewProps {
  type: string
  /** Everything SofaBlueprint draws from (form `dimensions` shape). */
  dimensions: React.ComponentProps<typeof SofaBlueprint>['dimensions']
  values: DimValues
  errors?: DimErrors
  onCommit: (key: BlueprintDimKey, value: number | null) => void
  onCompositionExtentChange?: (extent: {
    width: number
    height: number
  }) => void
}

/**
 * The 2D blueprint with Length / Width / Backrest / Armrest edited directly
 * on the drawing. Shared by the sofa module create and edit forms; must be
 * rendered inside their <Form> (FormProvider).
 */
export default function SofaDimensionsBlueprintField({
  id,
  changeableProperties,
  propertiesType,
  height = 460,
  debug = false,
  fillOccupancy,
  maxFillScale,
}: SofaDimensionsBlueprintFieldProps) {
  const form = useFormContext()
  const type: string = form.watch('type')
  const dimensions = form.watch('dimensions') || {}
  const { errors } = form.formState

  const values: DimValues = {
    width: dimensions.width,
    length: dimensions.length,
    backrest_width: dimensions.backrest_width,
    armrest_width: dimensions.armrest_width,
    extendable_part_length: dimensions.extendable_part_length,
    corner_part_length: dimensions.corner_part_length,
    corner_radius: dimensions.corner_radius,
    angle: dimensions.angle,
  }
  const dimErrors = (errors.dimensions ?? {}) as Partial<
    Record<BlueprintDimKey, { message?: string }>
  >
  const fieldErrors: DimErrors = Object.fromEntries(
    (Object.keys(DIM_LABELS) as BlueprintDimKey[]).map(key => [
      key,
      dimErrors[key]?.message,
    ])
  )

  const commit = useCallback(
    (key: BlueprintDimKey, value: number | null) => {
      form.setValue(`dimensions.${key}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [form]
  )

  const handleCompositionExtent = useCallback(
    ({ width, height }: { width: number; height: number }) => {
      if (form.getValues('type') !== 'COMPOSITE') return
      if (form.getValues('dimensions.width') !== width) {
        form.setValue('dimensions.width', width, { shouldDirty: true })
      }
      if (form.getValues('dimensions.length') !== height) {
        form.setValue('dimensions.length', height, { shouldDirty: true })
      }
    },
    [form]
  )

  return (
    <BlueprintDimensionsEditor
      id={id}
      type={type}
      dimensions={{
        ...dimensions,
        // SofaBlueprint/SofaElementProps require real numbers to draw;
        // form state allows null while a member's width/height is being
        // edited, so coerce at this render boundary like onSubmit does.
        composition:
          dimensions?.composition?.map(
            (entry: { width?: number | null; height?: number | null }) => ({
              ...entry,
              width: entry.width ?? 0,
              height: entry.height ?? 0,
            })
          ) ?? undefined,
      }}
      values={values}
      errors={fieldErrors}
      onCommit={commit}
      onCompositionExtentChange={handleCompositionExtent}
      changeableProperties={changeableProperties}
      propertiesType={propertiesType}
      height={height}
      debug={debug}
      fillOccupancy={fillOccupancy}
      maxFillScale={maxFillScale}
    />
  )
}

/**
 * Form-agnostic core: the fill-mode blueprint, its dimension overlay, the
 * under-canvas fallback row and the error list. Also used per member by
 * CompositeMembersEditor.
 */
export function BlueprintDimensionsEditor({
  id,
  type,
  dimensions,
  values,
  errors: fieldErrors = {},
  onCommit: commit,
  onCompositionExtentChange,
  changeableProperties,
  propertiesType,
  height = 460,
  debug = false,
  fillOccupancy,
  maxFillScale,
}: BlueprintDimensionsEditorProps) {
  const resolve = (layout: BlueprintLayout) =>
    resolveAnchors({
      layout: { ...layout, ready: layout.ready && propertiesType === type },
      changeable: changeableProperties,
      values,
    })

  // Mirrors the blueprint's measured layout so the fallback row (keys that
  // can't be placed on this shape yet) can be rendered outside the canvas.
  const [layout, setLayout] = useState<BlueprintLayout | null>(null)
  const fallbackKeys = layout ? resolve(layout).fallbackKeys : []

  const renderOverlay = (overlayLayout: BlueprintLayout) => (
    <BlueprintDimensionOverlay
      layout={overlayLayout}
      anchors={resolve(overlayLayout).anchors}
      values={values}
      errors={fieldErrors}
      placeholders={
        overlayLayout.scale > 0
          ? {
              width: String(
                Math.round(overlayLayout.shapeRect.width / overlayLayout.scale)
              ),
              length: String(
                Math.round(overlayLayout.shapeRect.height / overlayLayout.scale)
              ),
            }
          : undefined
      }
      onCommit={commit}
      debugRect={debug}
    />
  )

  const errorLines = (Object.keys(DIM_LABELS) as BlueprintDimKey[])
    .filter(key => fieldErrors[key])
    .map(key => `${DIM_LABELS[key]}: ${fieldErrors[key]}`)

  return (
    <div className="space-y-2">
      <div className="rounded-md border bg-white">
        <SofaBlueprint
          id={id}
          dimensions={dimensions}
          type={type}
          fit="fill"
          height={height}
          fillOccupancy={fillOccupancy}
          maxFillScale={maxFillScale}
          renderOverlay={renderOverlay}
          onLayoutChange={setLayout}
          onCompositionExtentChange={onCompositionExtentChange}
        />
      </div>
      {fallbackKeys.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
          {fallbackKeys.map(key => (
            <label key={key} className="flex items-center gap-1.5">
              <span>{DIM_LABELS[key]}</span>
              <BlueprintDimensionInput
                label={DIM_LABELS[key]}
                value={values[key]}
                error={fieldErrors[key]}
                onCommit={v => commit(key, v)}
              />
            </label>
          ))}
        </div>
      )}
      {errorLines.length > 0 && (
        <ul className="text-xs font-medium text-destructive">
          {errorLines.map(line => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
